/**
 * useNotify — show a toast (the bottom-right pop-up) for an outcome.
 *
 *   const notify = useNotify();
 *   try {
 *     await doSomething().unwrap();
 *     notify.success('Instance added successfully.');
 *   } catch (error) {
 *     notify.error(error, 'Could not add the instance.');
 *   }
 *
 * `error` takes whatever the catch received — RTK Query error, thrown Error,
 * failure envelope, or a plain string — and shows the server's own message when
 * there is one (services/apiMessages.js), the fallback otherwise.
 *
 * Built on the design system's ToastProvider (mounted in app/providers). The
 * app's replacement for legacy's global `updateSnackData` snackbar.
 */
import { useMemo } from 'react';

import { useToast } from '@components/ui';
import { readApiError } from '@services/apiMessages';

/** Errors stay a little longer than successes — they usually need reading. */
const SUCCESS_MS = 4000;
const ERROR_MS = 6000;
/** Legacy's warning snackbar timeout — the text is a sentence to read. */
const WARNING_MS = 10000;

export function useNotify() {
  const { toast } = useToast();

  return useMemo(
    () => ({
      success: (title, description) =>
        toast({ variant: 'success', title, description, duration: SUCCESS_MS }),
      info: (title, description) =>
        toast({ variant: 'info', title, description, duration: SUCCESS_MS }),
      // "Not now" rather than "failed": amber, held longer. The library has no
      // warning variant; styles/global.css gives `amd-toast--warning` its
      // amber edge and triangle icon (the app's toast design).
      warning: (title, description) =>
        toast({ variant: 'warning', title, description, duration: WARNING_MS }),
      // A request that ended the session (services/interceptors.js) is already
      // explained by SessionNotice's one "Session expired" toast — showing it
      // again for every request that was in flight would stack copies.
      error: (error, fallback) => {
        if (error?.normalized?.sessionEnded) return;
        toast({ variant: 'danger', title: readApiError(error, fallback), duration: ERROR_MS });
      },
    }),
    [toast],
  );
}
